/**
 * Draft View — unified entry point for Standard, Pre-rift, and Collection drafts.
 *
 * Phases:
 *   1. Format selection (Standard / Pre-rift / Collection)
 *   2. Configuration + pool generation
 *   3. Deck building (renders DeckBuilderView in limited mode)
 */
import { useState, useEffect } from 'react';
import { postJson, fetchJson } from './api.js';
import CardTile from './CardTile.jsx';
import DeckBuilderView from './DeckBuilderView.jsx';

const DRAFT_FORMATS = [
  { id: 'standard', label: 'Standard', desc: 'Open booster packs and build a 25-card deck from the pool.' },
  { id: 'pre-rift', label: 'Pre-rift', desc: 'Pick a champion kit, open 5 boosters, and build from the combined pool.' },
  { id: 'collection', label: 'Collection', desc: 'Draft from your own collection — multiple seats supported.' }
];

export default function DraftView() {
  const [format, setFormat] = useState(null);     // null = format picker
  const [phase, setPhase] = useState('config');     // 'config' | 'building'

  // Standard config
  const [setCode, setSetCode] = useState('OGN');
  const [packs, setPacks] = useState(6);

  // Pre-rift config
  const [miniSetsLoading, setMiniSetsLoading] = useState(false);
  const [selectedMiniSet, setSelectedMiniSet] = useState(null);
  const [selectedDeckId, setSelectedDeckId] = useState(null);

  // Collection config
  const [players, setPlayers] = useState(2);
  const [packsPerPlayer, setPacksPerPlayer] = useState(5);
  const [activeSeat, setActiveSeat] = useState(0);

  // Shared
  const [pool, setPool] = useState(null);
  const [pools, setPools] = useState(null);  // for collection multi-seat
  const [initialDeck, setInitialDeck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load mini-sets when Pre-rift is selected
  useEffect(() => {
    if (format !== 'pre-rift') return;
    setMiniSetsLoading(true);
    fetchJson('/api/mini-sets')
      .then((data) => {
        // Auto-select first mini-set (only SFD available)
        if (data.miniSets?.length) {
          setSelectedMiniSet(data.miniSets[0]);
          setSetCode(data.miniSets[0].setCode);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setMiniSetsLoading(false));
  }, [format]);

  // --- Handlers ---

  const runStandard = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await postJson('/api/draft/standard', { set: setCode, packs });
      setPool(result.pool || []);
      setInitialDeck(null);
      setPhase('building');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runPreRift = async () => {
    if (!selectedDeckId) {
      setError('Please select a champion kit.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await postJson('/api/draft/pre-rift', {
        set: setCode,
        deckId: selectedDeckId
      });
      setPool(result.pool || []);
      setInitialDeck(result.miniDeck || null);
      setPhase('building');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runCollection = async () => {
    setError(null);
    setLoading(true);
    try {
      const check = await postJson('/api/draft/collection/check', {
        players,
        packsPerPlayer,
        cardsPerPack: 14
      });
      if (!check.ok) {
        setError(`Not enough cards. Need ${check.required}, have ${check.owned}.`);
        setLoading(false);
        return;
      }
      const result = await postJson('/api/draft/collection/run', {
        players,
        packsPerPlayer,
        cardsPerPack: 14
      });
      const allPools = result.pools || [];
      setPools(allPools);
      setActiveSeat(0);
      setPool(allPools[0] || []);
      setInitialDeck(null);
      setPhase('building');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setPhase('config');
    setPool(null);
    setPools(null);
    setInitialDeck(null);
    setSelectedDeckId(null);
    setError(null);
  };

  const handleFormatBack = () => {
    setFormat(null);
    setPhase('config');
    setPool(null);
    setPools(null);
    setInitialDeck(null);
    setSelectedDeckId(null);
    setError(null);
  };

  const handleSeatChange = (seatIndex) => {
    if (!pools || seatIndex < 0 || seatIndex >= pools.length) return;
    setActiveSeat(seatIndex);
    setPool(pools[seatIndex]);
    setInitialDeck(null);
  };

  // --- Phase: deck building ---
  if (phase === 'building' && pool) {
    const deckFormat = format === 'pre-rift' ? 'pre-rift' : 'standard';
    return (
      <div className="draft-view">
        {/* Seat tabs for collection draft */}
        {format === 'collection' && pools && pools.length > 1 && (
          <div className="draft-seat-tabs">
            {pools.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`draft-seat-tab ${i === activeSeat ? 'active' : ''}`}
                onClick={() => handleSeatChange(i)}
              >
                Player {i + 1}
              </button>
            ))}
          </div>
        )}
        <DeckBuilderView
          key={`${format}-${activeSeat}`}
          mode="limited"
          pool={pool}
          format={deckFormat}
          initialDeck={initialDeck}
          onBack={handleBack}
        />
      </div>
    );
  }

  // --- Phase: format picker ---
  if (!format) {
    return (
      <div className="draft-view">
        <div className="gallery-header">
          <h1>Draft</h1>
          <p className="gallery-stats">Choose a draft format to begin</p>
        </div>
        <div className="draft-format-picker">
          {DRAFT_FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="draft-format-card"
              onClick={() => setFormat(f.id)}
            >
              <span className="draft-format-title">{f.label}</span>
              <span className="draft-format-desc">{f.desc}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- Phase: config (format-specific) ---
  return (
    <div className="draft-view">
      <div className="gallery-header">
        <h1>
          {format === 'standard' && 'Standard Draft'}
          {format === 'pre-rift' && 'Pre-rift Draft'}
          {format === 'collection' && 'Collection Draft'}
        </h1>
        <button type="button" className="clear-filters-button" onClick={handleFormatBack}>
          ← Change format
        </button>
      </div>

      {error && <p className="gallery-error">{error}</p>}

      {/* Standard config */}
      {format === 'standard' && (
        <div className="draft-config">
          <div className="draft-config-row">
            <label className="draft-label">
              Set:
              <select value={setCode} onChange={(e) => setSetCode(e.target.value)} className="filter-select">
                <option value="OGN">Origins</option>
                <option value="SFD">Spiritforged</option>
              </select>
            </label>
            <label className="draft-label">
              Packs:
              <input
                type="number"
                min={1}
                max={10}
                value={packs}
                onChange={(e) => setPacks(parseInt(e.target.value, 10) || 6)}
                className="filter-select"
              />
            </label>
          </div>
          <p className="draft-info">
            Open {packs} booster packs from {setCode === 'OGN' ? 'Origins' : 'Spiritforged'} and build a 25-card limited deck.
            Pack odds use the same slot structure and treatment system as regular pack opening.
          </p>
          <button type="button" className="search-button" disabled={loading} onClick={runStandard}>
            {loading ? 'Opening packs...' : `Open ${packs} Packs & Build`}
          </button>
        </div>
      )}

      {/* Pre-rift config */}
      {format === 'pre-rift' && (
        <div className="draft-config">
          {miniSetsLoading && <p className="draft-info">Loading champion kits...</p>}

          {!miniSetsLoading && selectedMiniSet && (
            <>
              <p className="draft-info">
                Choose your champion kit. You will receive the kit&apos;s preconstructed cards plus 5 booster packs
                from {selectedMiniSet.setCode === 'SFD' ? 'Spiritforged' : selectedMiniSet.setCode}.
              </p>
              <div className="draft-legend-picker">
                {(selectedMiniSet.decks || []).map((deck) => (
                  <button
                    key={deck.id}
                    type="button"
                    className={`draft-legend-card ${selectedDeckId === deck.id ? 'selected' : ''}`}
                    onClick={() => setSelectedDeckId(deck.id)}
                  >
                    <CardTile card={deck.legend} />
                    <span className="draft-legend-name">{deck.name}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="search-button"
                disabled={loading || !selectedDeckId}
                onClick={runPreRift}
              >
                {loading ? 'Generating pool...' : 'Pick Kit & Open Packs'}
              </button>
            </>
          )}

          {!miniSetsLoading && !selectedMiniSet && (
            <p className="draft-info">No mini-sets available. Pre-rift requires a champion mini-set (currently Spiritforged only).</p>
          )}
        </div>
      )}

      {/* Collection config */}
      {format === 'collection' && (
        <div className="draft-config">
          <div className="draft-config-row">
            <label className="draft-label">
              Players:
              <input
                type="number"
                min={1}
                max={8}
                value={players}
                onChange={(e) => setPlayers(parseInt(e.target.value, 10) || 2)}
                className="filter-select"
              />
            </label>
            <label className="draft-label">
              Packs per player:
              <input
                type="number"
                min={1}
                max={10}
                value={packsPerPlayer}
                onChange={(e) => setPacksPerPlayer(parseInt(e.target.value, 10) || 5)}
                className="filter-select"
              />
            </label>
          </div>
          <p className="draft-info">
            Build mock packs ({players} × {packsPerPlayer} × 14 = {players * packsPerPlayer * 14} cards needed) from your collection.
            Each player gets their own pool to build from.
          </p>
          <button type="button" className="search-button" disabled={loading} onClick={runCollection}>
            {loading ? 'Building pools...' : 'Run Collection Draft'}
          </button>
        </div>
      )}
    </div>
  );
}
